import React, { useRef, useState, useEffect } from 'react';
import { XIcon } from '@heroicons/react/solid';
import NextScript from 'next/script';
import { useImageInput } from '@hooks/files/useImageInput';
import { useImageModalContext } from './ImageSelectModal';
import { DropboxSVG } from '@components/base/svgs/DropboxSVG';
import {
    AnimatedImageCropper,
    ImageModalFooter,
    MultiFileInputView,
    ShowSingleFileInput,
} from './SharedComponents';
import { useFileTypeImage } from '@components/file/fileUtilities';

const DROPBOX_APPKEY = process.env.NEXT_PUBLIC_DROPBOX_APPKEY || '1yrn85aq4kjyx4k';
/**
 * API Docs: https://www.dropbox.com/developers/chooser
 *
 * @param param0
 * @returns
 */
export function UploadDropbox({ toggle }: { toggle: any }) {
    const {
        fileCreate,
        authorizer,
        getContentMimeTypes,
        fileList,
        isImmediateUpdate,
        isMultiFile,
    } = useImageModalContext();
    const { fileInput, crop, handlers } = useImageInput({
        immediateUpdate: isImmediateUpdate,
        updateNow: (file: any) => {
            fileCreate.mutate({
                title: file.name ?? `file-${Date.now()}`,
                srcUrl: file?.link || '',
                authorizerItem: authorizer,
            });
        },
        multiple: isMultiFile,
    });

    const dropboxRef = useRef<any>(
        (() => {
            if ((window as any).Dropbox) {
                return (window as any).Dropbox;
            }
            return null;
        })(),
    );
    const [initialized, setInitialized] = useState(false);

    const extensions = getContentMimeTypes('dropbox');

    const [click, setClick] = useState<(() => void) | null>(
        dropboxRef.current !== null
            ? () => {
                  return () => {
                      dropboxRef.current?.choose({
                          success: function (files: any) {
                              //   handlers.createFileInput(files[0]);
                              if (isMultiFile) {
                                  handlers.createFilesAll(files);
                              } else {
                                  handlers.getLink(files[0]);
                              }
                          },
                          cancel: function () {},
                          linkType: 'direct',
                          extensions: extensions,
                          folderselect: false,
                          multiselect: isMultiFile,
                          // Size of File in Bytes
                          sizeLimit: 1024 * 1024 * 10,
                      });
                  };
              }
            : null,
    );
    useEffect(() => {
        if (initialized) {
            setClick(() => () => {
                dropboxRef.current?.choose({
                    success: function (files: any) {
                        // handlers.createFileInput(files[0]);

                        if (isMultiFile) {
                            handlers.createFilesAll(files);
                        } else {
                            handlers.getLink(files[0]);
                        }
                    },
                    cancel: function () {},
                    linkType: 'direct',
                    extensions: extensions,
                    folderselect: false,
                    multiselect: isMultiFile,
                    sizeLimit: 1024 * 1024 * 10,
                });
            });
        }
    }, [initialized, isMultiFile]);

    const selectIcon = useFileTypeImage();
    const showMultiple = isMultiFile && !!fileList?.length;
    const showSingle = !isMultiFile && !!fileInput;
    const showDefault = !showMultiple && !showSingle;
    const likelyImage = fileInput && fileInput?.localUrl?.search?.(/(\.(png|jpeg|jpg|svg))/) >= 0;
    return (
        <div className="relative flex flex-col flex-1 overflow-hidden">
            <NextScript
                id="dropboxjs"
                src="https://www.dropbox.com/static/api/2/dropins.js"
                data-app-key={DROPBOX_APPKEY}
                onLoad={() => {
                    dropboxRef.current = (window as any).Dropbox;
                    setInitialized(true);
                }}
            />

            <div className="w-full h-full flex flex-1 flex-col">
                <div className="relative px-4 py-3 flex items-center justify-center border-b border-gray-200">
                    <div className="flex items-center justify-center">
                        <DropboxSVG className="fill-current text-dropboxBlue w-8 h-8" />
                    </div>
                    <button
                        type="button"
                        onClick={() => toggle(false)}
                        className="absolute px-3 flex items-center justify-center inset-y right-2 focus:outline-primary">
                        <XIcon className="w-5 h-5 fill-gray-500 stroke-gray-500 hover:fill-teal-900 hover:stroke-teal-900 font-bold" />
                    </button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center w-full">
                    {showSingle && (
                        <div className="flex flex-1 items-center justify-center pt-6 w-full">
                            {likelyImage && (
                                <AnimatedImageCropper
                                    selected={{ url: fileInput?.localUrl }}
                                    handlers={handlers}
                                    crop={crop}
                                    close={close}
                                    onClickHeader={() => toggle(false)}
                                />
                            )}
                            {!likelyImage && (
                                <ShowSingleFileInput
                                    fileInput={fileInput}
                                    selectIcon={selectIcon}
                                    forceType="video"
                                />
                            )}
                        </div>
                    )}
                    {showMultiple && (
                        <div className="relative w-full h-full flex-col justify-center items-center grid grid-cols-8">
                            <MultiFileInputView
                                fileList={fileList}
                                fileCreate={fileCreate}
                                selectIcon={selectIcon}
                            />
                        </div>
                    )}
                    {showDefault && (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                            <div className=" bg-dropboxBlue p-4 rounded-full">
                                <DropboxSVG className="fill-current text-white w-12 h-12" />
                            </div>
                            <div className="py-8">
                                <h2 className="text-2xl">Select Files from Dropbox</h2>
                            </div>
                            <div className="flex flex-col text-center gap-y-1 text-gray-500">
                                <p>You need to authenticate with Dropbox.</p>
                                <p>We only extract images and never modify or delete them.</p>
                            </div>
                            <div className="text-center flex items-center py-6">
                                <button
                                    type="button"
                                    onClick={click ?? (() => {})}
                                    className="text-center focus:outline-none text-white bg-dropboxBlue px-4 py-3 rounded-sm">
                                    Connect Dropbox
                                </button>
                            </div>
                            <div className="flex flex-col text-center gap-y-1 text-gray-500">
                                <p>A new page will open to connect your account.</p>
                                <p>{`To disconnect from Dropbox click "Sign Out" button in the menu.`}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ImageModalFooter />
        </div>
    );
}
